import React, { useEffect, useState } from "react";
import {
  StyledProjectForm,
  StyledContainer,
  ProjectFormStyles,
} from "./styles";
import { translate } from "../../utils/translations/translate";
import ProjectResourceDetails from "./ProjectResourceDetails";
import ProjectClientDetails from "./ProjectClientDetails";
import ProjectVendor from "./ProjectVendor";
import ProjectPoDetails from "./ProjectPoDetails";
import ProjectAttachments from "./ProjectAttachements";
import Button from "../UI/Button";
import { useFormik } from "formik";
import { ProjectValidationSchema } from "./utils/schema";
import { useProjects } from "./utils/hooks/useProjects";
import { Loader } from "../Loader/Loader";
import { useNavigate } from "react-router-dom";
import { SITE_LINKS_SCHEMA } from "../../router/navlinks";
import { Alert } from "../UI/Alert";
import { TOAST_TIMER } from "../../utils/constants";
import { ConfirmationModal } from "../UI/ConfirmationModal";
const CreateEditProjectForm = ({ mode, projectId }) => {
  const {
    isLoading,
    error,
    createNewProject,
    fetchProjectDetails,
    updateProject,
    deleteAttachment,
    attachmentToast,
    setAttachmentToast,
    hideServiceError,
    handleAttachmentToastClose,
  } = useProjects();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const navigate = useNavigate();
  const formMode = ["add", "edit"].includes(mode) ? mode : "add";
  const isCreateMode = formMode === "add";
  const formTitle = isCreateMode
    ? translate("pageTitles.newProject")
    : translate("pageTitles.updateProject");
  const successToastMessage = isCreateMode
    ? translate("labels.projectCreated")
    : translate("labels.projectUpdated");
  const errorToastMessage = isCreateMode
    ? translate("labels.projectCreationError")
    : translate("labels.projectUpdateError");
  const [isFormReadonlyMode, setIsFormReadOnlyMode] = useState(false);
  const formik = useFormik({
    initialValues: {
      resourceId: "",
      resourceName: "",
      projectId: "",
      empType: "W2",
      managerName: "",
      managerId: "",
      status: "Active",
      projectName: "",
      contractualPath: "",
      clientName: "",
      clientAddress: "",
      clientManagerName: "",
      clientMangerTitle: "",
      clientManagerEmail: "",
      clientManagerPhone: "",
      vendorName: "",
      vendorAddress: "",
      timesheetTo: "",
      invoiceTo: "",
      msaSignatoryTitle: "",
      msaSignatoryName: "",
      msaSignatoryEmail: "",
      msaSignatoryPhone: "",
      recruiterName: "",
      recruiterEmail: "",
      recruiterPhone: "",
      recruiterTitle: "",
      accountingContactName: "",
      accountingContactEmail: "",
      accountingContactPhone: "",
      accountingContactTitle: "",
      escalationContactName: "",
      escalationContactEmail: "",
      escalationContactPhone: "",
      escalationContactTitle: "",
      poStartDate: "",
      poEndDate: "",
      paymentTerms: "NET30",
      billRate: "",
      invoiceFrequency: "Weekly",
      poMsa: "",
      po_wo_sow_: "",
      vendorLetter: "",
      coi: "",
      otherAttachments: "",
      savedAttachments: {
        msa: [],
        poWoSOW: [],
        vendorLetter: [],
        coi: [],
        other: [[]],
      },
    },
    validationSchema: ProjectValidationSchema,
    onSubmit: (values) => {
      if (isCreateMode) {
        createNewProject(values).then((res) => {
          if (String(res ?.StatusCode) === "200") {
            setShowSuccessToast(true);
            window.location.href = SITE_LINKS_SCHEMA.PROJECT_ENGAGEMENTS;
          }
        });
      } else {
        updateProject(values).then((res) => {
          if (String(res ?.StatusCode) === "200") {
            setShowSuccessToast(true);
            window.location.href = SITE_LINKS_SCHEMA.PROJECT_ENGAGEMENTS;
            if (values.status ?.toLowerCase() === "inactive") {
              setIsFormReadOnlyMode(true);
            } else {
              setIsFormReadOnlyMode(false);
            }
          }
        });
      }
    },
  });
  const { errors, isSubmitting, isValidating, setValues } = formik;
  useEffect(() => {
    if (isSubmitting && !isValidating) {
      let keys = Object.keys(errors);
      if (keys.length > 0) {
        const errorElement = document.querySelector(`[name="${keys[0]}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  }, [errors, isSubmitting, isValidating]);

  useEffect(() => {
    if (!projectId) return;
    fetchProjectDetails(projectId).then((projDetails) => {
      if (projDetails) {
        if (projDetails ?.status ?.toLowerCase() === "inactive") {
          setIsFormReadOnlyMode(true);
        }
        setValues(projDetails);
      }
    });
  }, [projectId, fetchProjectDetails, setValues]);
  return (
    <StyledProjectForm onSubmit={formik.handleSubmit}>
      {showSuccessToast && (
        <Alert
          type="success"
          message={successToastMessage}
          onToastClose={() => {
            setShowSuccessToast(false);
          }}
        />
      )}
      {error && (
        <Alert
          type="error"
          message={`${errorToastMessage} ${error}`}
          onToastClose={hideServiceError}
        />
      )}
      <header className="tma-page-header">
        <h1>{formTitle}</h1>
      </header>
      <StyledContainer>
        <section className="tma_prj_resource">
          <ProjectResourceDetails
            isReadOnlyMode={isFormReadonlyMode}
            formik={formik}
            isCreateMode={isCreateMode}
          />
        </section>
        <section className="tma_prj_client">
          <ProjectClientDetails
            formik={formik}
            isReadOnlyMode={isFormReadonlyMode}
          />
        </section>
        <section className="tma_prj_vendor">
          <ProjectVendor formik={formik} isReadOnlyMode={isFormReadonlyMode} />
        </section>
        <section className="tma_prj_po">
          <ProjectPoDetails
            formik={formik}
            isReadOnlyMode={isFormReadonlyMode}
          />
        </section>
        <section className="tma_prj_attachments">
          <ProjectAttachments
            isReadOnlyMode={isFormReadonlyMode}
            formik={formik}
            projectId={projectId}
            onDeleteAttachment={deleteAttachment}
            setToastInfo={setAttachmentToast}
            toastInfo={attachmentToast}
            onToastClose={handleAttachmentToastClose}
          />
        </section>
        <section className="tma_form_actions">
          <Button
            type="button"
            className={"primary-outline"}
            text={translate("labels.cancel")}
            onClick={() => {
              setShowExitModal(true);
            }}
          />
          <Button
            type="submit"
            className={"primary-success"}
            text={translate("labels.save")}
            variantStyle="primary"
          />
        </section>
        {isLoading && <Loader />}
      </StyledContainer>
      <ProjectFormStyles />
      {showExitModal && (
        <ConfirmationModal
          title={translate("labels.projectExitModalTitle")}
          infoText={translate("labels.projectExitModalInfo")}
          onClose={() => {
            setShowExitModal(false)
          }}
          confirmBtnText={translate("labels.projectExitConfirmBtnText")}
          cancelBtnText={translate("labels.projectExitCancelBtnText")}
          onConfirm={() => {
            navigate(SITE_LINKS_SCHEMA.PROJECT_ENGAGEMENTS)
          }}
        />
      )}
    </StyledProjectForm>
  );
};

export default CreateEditProjectForm;
